import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';
import ReactModal from 'react-modal';

// components
import Table from '../Table';
import Search from '../../../assets/images/svg/search.svg';

// icons
import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';

// Tools
import { dateFormat } from '../../tools/manager';


const TABS = {
    ORDER: 'ORDER',
    INTERVENTION: 'INTERVENTION',
    PREFILL: 'PREFILL',
};

const INITIAL_TAB_STATE = {
    items: [],
    nextToken: null,
    loading: false,
    initialized: false,
};

const HEADERS = [
    // 'Type',
    'Réalisé le',
    'Fait par',
    'Numéro',
    'Type de chantier',
    'Nom du client',
    'Nom du chantier',
    'Statut',
    { label: 'Actions', className: 'text-right' },
];


const Coupons = () => {
    const navigate = useNavigate();

    // 🌱 Easy-Peasy actions
    const listAllOrdersCoupons = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.listAllOrdersCoupons
    );
    const listAllInterventionCoupons = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.listAllInterventionCoupons
    );
    const listAllPrefillCoupons = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.listAllPrefillCoupons
    );

    const deleteOrderCoupon = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.removeOrderCoupon
    );
    const deleteInterventionCoupon = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.removeInterventionCoupon
    );
    const deletePrefillCoupon = useStoreActions(
        (actions) => actions.middleware.OrdersMiddleware.removePrefillCoupon
    );

    const getUser = useStoreActions(
        (actions) => actions.middleware.AdminMiddleware.getAdmin
    );


    // 🧠 Config par onglet (label, requêtes, routes…)
    const TAB_CONFIG = {
        [TABS.ORDER]: {
            label: 'Bon de commande',
            placeholder: 'Recherche bon de commande',
            list: listAllOrdersCoupons,
            delete: deleteOrderCoupon,
            worksiteKey: 'worksite',
            route: (id) => `/pdf-preview/order/${id}`,
            enrich: async (items) =>
                Promise.all(
                    items.map(async (el) => {
                        if (!el?.collaborator && el?.orderCouponCollaboratorId) {
                            const user = await getUser(el.orderCouponCollaboratorId);
                            return {
                                ...el,
                                collaborator: {
                                    first_name: user?.first_name || '',
                                    last_name: user?.last_name || '',
                                },
                            };
                        }
                        return el;
                    })
                ),
        },
        [TABS.INTERVENTION]: {
            label: "Bon d'intervention",
            placeholder: 'Recherche bon d’intervention',
            list: listAllInterventionCoupons,
            delete: deleteInterventionCoupon,
            worksiteKey: 'worksite',
            route: (id) => `/pdf-preview/intervention/${id}`,
        },
        [TABS.PREFILL]: {
            label: 'Bon pré-rempli',
            placeholder: 'Recherche bon pré-rempli',
            list: listAllPrefillCoupons,
            delete: deletePrefillCoupon,
            worksiteKey: 'worksites',
            route: (id) => `/pdf-preview/Pre-intervention/${id}`,
        },
    };

    // 🔥 Modal de suppression
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState(null);

    // 🌱 États
    const [searchParams, setSearchParams] = useSearchParams();

    const getValidTab = (value) =>
        Object.values(TABS).includes(value) ? value : TABS.ORDER;

    const [activeTab, setActiveTab] = useState(() =>
        getValidTab(searchParams.get('tab'))
    );
    const [search, setSearch] = useState('');
    const [stateByTab, setStateByTab] = useState({
        [TABS.ORDER]: { ...INITIAL_TAB_STATE },
        [TABS.INTERVENTION]: { ...INITIAL_TAB_STATE },
        [TABS.PREFILL]: { ...INITIAL_TAB_STATE },
    });

    const currentConfig = TAB_CONFIG[activeTab];
    const currentState = stateByTab[activeTab];

    // ✅ Revision maps (ORDER + INTERVENTION uniquement)
    // - Ancien bon: coupon.revisionOfId = id du nouveau bon
    // - Nouveau bon: son id est référencé par revisionOfId d’un ancien bon
    const revisionMaps = useMemo(() => {
        const enabled =
            activeTab === TABS.ORDER || activeTab === TABS.INTERVENTION;

        if (!enabled) {
            return { enabled: false, newById: new Map(), oldByNewId: new Map() };
        }

        const items = currentState?.items || [];

        const newById = new Map();
        for (const c of items) {
            if (c?.id) newById.set(c.id, c);
        }

        const oldByNewId = new Map();
        for (const oldCoupon of items) {
            const newId = oldCoupon?.revisionOfId;
            if (newId) oldByNewId.set(newId, oldCoupon);
        }

        return { enabled: true, newById, oldByNewId };
    }, [activeTab, currentState?.items]);


    const updateCurrentTabState = (patch) => {
        setStateByTab((prev) => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab],
                ...(typeof patch === 'function' ? patch(prev[activeTab]) : patch),
            },
        }));
    };

    const openDeleteModal = (coupon) => {
        setCouponToDelete(coupon);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setCouponToDelete(null);
    };

    // 📥 Chargement initial de l'onglet sélectionné
    useEffect(() => {
        if (!currentState.initialized) {
            handleLoadMore();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // 🔄 Synchronisation de l’onglet actif avec l’URL
    useEffect(() => {
        const urlTab = searchParams.get('tab');
        const validTab = getValidTab(urlTab);
        if (validTab !== activeTab) {
            setActiveTab(validTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // 📥 Fonction générique de chargement (pagination)
    const handleLoadMore = async () => {
        if (currentState.loading) return;

        const limit = 25;
        const { list, enrich } = currentConfig;

        updateCurrentTabState({ loading: true });

        try {
            const res = await list({
                limit,
                nextToken: currentState.nextToken,
            });

            let items = res.items || [];
            if (enrich) {
                items = await enrich(items);
            }

            updateCurrentTabState((prev) => ({
                items: [...prev.items, ...items],
                nextToken: res.nextToken,
                loading: false,
                initialized: true,
            }));
        } catch (err) {
            console.error(err);
            toast.error(
                '❗️ Une erreur est survenue lors du chargement de la liste des bons'
            );
            updateCurrentTabState({ loading: false, initialized: true });
        }
    };

    // 🗑️ Suppression d’un bon (confirmée depuis le modal)
    const confirmDeleteCoupon = async () => {
        if (!couponToDelete) return;

        try {
            await currentConfig.delete(couponToDelete.id);

            updateCurrentTabState((prev) => ({
                items: prev.items.filter((item) => item.id !== couponToDelete.id),
            }));
            toast.success('✅ Bon supprimé avec succès');
        } catch (err) {
            console.error(err);
            toast.error('❗️ Erreur lors de la suppression du bon');
        } finally {
            closeDeleteModal();
        }
    };

    // 👉 Détermine si le bon est "à classer"
    const needsClassification = (coupon) => {
        const worksite = coupon[currentConfig.worksiteKey] || {};
        const hasCompany = !!worksite?.company?.name;
        const hasWorksiteName = !!worksite?.name;

        // tu peux ajuster la condition selon ton vrai modèle
        return !(hasCompany && hasWorksiteName);
    };

    // 🔍 Filtre de recherche
    const filteredItems = useMemo(() => {
        const { items } = currentState;
        if (!search.trim()) return items;

        const lower = search.toLowerCase();

        return items.filter((el) => {
            const date = el.createdAt || el.dateSigned;
            const formatted = date ? dateFormat(date) : null;

            const worksite = el.worksite || el.worksites || {};
            const fieldsToCheck = [
                worksite?.company?.name,
                worksite?.name,
                worksite?.type?.name,
                el?.numero,
                el?.collaborator?.first_name,
                el?.collaborator?.last_name,
            ]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase());

            if (formatted) {
                fieldsToCheck.push(
                    formatted.day,
                    formatted.month,
                    formatted.year
                );
            }

            return fieldsToCheck.some((field) => field.includes(lower));
        });
    }, [search, currentState]);

    // 📄 Navigation vers le PDF
    const handleRowClick = (coupon) => {
        if (coupon.ctm === 'true') return;
        navigate(currentConfig.route(coupon.id));
    };

    const getCouponTypeLabel = () => currentConfig.label;

    return (
        <section className="md:pt-8 px-4 md:px-9">
            {/* Onglets */}
            <div className="flex gap-3 mt-4 border-b border-gray-200">
                {Object.values(TABS).map((tabKey) => {
                    const cfg = TAB_CONFIG[tabKey];
                    const isActive = activeTab === tabKey;

                    return (
                        <button
                            key={tabKey}
                            className={`pb-2 px-3 text-sm md:text-base ${isActive
                                ? 'border-b-2 border-primary-blue font-semibold text-primary-blue'
                                : 'text-gray-500'
                                }`}
                            onClick={() => {
                                setActiveTab(tabKey);
                                setSearchParams({ tab: tabKey });
                            }}
                        >
                            {cfg.label}s
                        </button>
                    );
                })}
            </div>

            {/* Search bar */}
            <div className="relative flex mt-6 border border-gray-admin rounded-xl">
                <input
                    type="text"
                    placeholder={currentConfig.placeholder}
                    className="w-full py-3 pl-10 border-none rounded-xl placeholder:text-blue-admin inputFocus"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                <img
                    src={Search}
                    alt="search icon"
                    className="absolute left-0 flex items-center w-8 h-5 pl-3 top-4"
                />
            </div>

            {/* Table */}
            <div className="mt-9">
                <Table>
                    <thead>
                        <tr>
                            {HEADERS.map((header, index) => {
                                const label = typeof header === 'string' ? header : header.label;
                                const className = typeof header === 'string' ? '' : header.className;

                                return (
                                    <th key={index} className={className}>
                                        {label}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredItems?.length ? (
                            filteredItems
                                .slice()
                                .sort((a, b) => (b.numero || 0) - (a.numero || 0))
                                .map((coupon) => {
                                    const dateValue = coupon.createdAt || coupon.dateSigned;
                                    const date = dateValue ? dateFormat(dateValue) : null;

                                    const worksite = coupon[currentConfig.worksiteKey] || {};
                                    const typeName = worksite?.type?.name || '-';
                                    const companyName = worksite?.company?.name || coupon?.nameClient || '-';
                                    const worksiteName = worksite?.name || coupon?.nameWorksite || '-';

                                    const fullName = coupon?.collaborator
                                        ? `${coupon.collaborator.first_name || ''} ${coupon.collaborator.last_name || ''
                                            }`.trim()
                                        : '';
                                    const createdBy = coupon?.name;

                                    const toClassify = needsClassification(coupon);

                                    // 🔁 Revision info (uniquement ORDER / INTERVENTION)
                                    let revisionInfo = null;

                                    if (revisionMaps.enabled) {
                                        // Cas 1: ce bon est l'ancien (il a revisionOfId)
                                        if (coupon?.revisionOfId) {
                                            const newCoupon = revisionMaps.newById.get(coupon.revisionOfId) || null;
                                            revisionInfo = { kind: 'OLD', newCoupon };
                                        } else {
                                            // Cas 2: ce bon est le nouveau (un ancien pointe vers lui)
                                            const oldCoupon = revisionMaps.oldByNewId.get(coupon.id) || null;
                                            if (oldCoupon) revisionInfo = { kind: 'NEW', oldCoupon };
                                        }
                                    }


                                    return (
                                        <tr
                                            key={coupon.id}
                                            className={`h-16 border-b border-white-admin hover:bg-blue-50 transition-all ${coupon.ctm !== 'true' ? 'cursor-pointer' : ''
                                                }`}
                                            onClick={() => handleRowClick(coupon)}
                                        >
                                            {/* <td>{getCouponTypeLabel()}</td> */}
                                            <td>
                                                {date
                                                    ? `${date.day}.${date.month}.${date.year}`
                                                    : '-'}
                                            </td>
                                            <td>{fullName || createdBy || '-'}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="flex flex-col gap-1">
                                                    <div>
                                                        <span className="font-medium">
                                                            {coupon?.numero} {coupon.ctm === 'true' ? '- pré-rempli' : ''}
                                                        </span>
                                                    </div>

                                                    {/* ✅ Affichage revision (ORDER + INTERVENTION seulement) */}
                                                    {revisionInfo?.kind === 'OLD' && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-orange-100 text-orange-700">
                                                                Modifié
                                                            </span>

                                                            <span className="text-xs text-gray-600">
                                                                → remplacé par{' '}
                                                                <span className="font-semibold">
                                                                    {revisionInfo.newCoupon?.numero
                                                                        ? `N° ${revisionInfo.newCoupon.numero}`
                                                                        : 'un nouveau bon'}
                                                                </span>
                                                            </span>

                                                            {revisionInfo.newCoupon?.id && (
                                                                <button
                                                                    type="button"
                                                                    className="text-xs text-blue-admin underline"
                                                                    onClick={() => navigate(currentConfig.route(revisionInfo.newCoupon.id))}
                                                                >
                                                                    Voir le nouveau
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}

                                                    {revisionInfo?.kind === 'NEW' && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-700">
                                                                Révision
                                                            </span>

                                                            <span className="text-xs text-gray-600">
                                                                remplace{' '}
                                                                <span className="font-semibold">
                                                                    {revisionInfo.oldCoupon?.numero
                                                                        ? `N° ${revisionInfo.oldCoupon.numero}`
                                                                        : 'un ancien bon'}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{typeName || '-'}</td>
                                            <td>{companyName || '-'}</td>
                                            <td>{worksiteName || '-'}</td>
                                            {/* ✅ Statut / À classer */}
                                            <td onClick={(e) => e.stopPropagation()}>
                                                {toClassify ? (
                                                    <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                        À classer
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-50 text-green-600">
                                                        Classé
                                                    </span>
                                                )}
                                            </td>
                                            <td
                                                className="text-right"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div>
                                                    <button
                                                        onClick={() => openDeleteModal(coupon)}
                                                        className="w-8 p-2 border rounded-lg mt-7 border-white-admin"
                                                        type="button"
                                                    >
                                                        <img src={DeleteIcon} alt="delete icon" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                        ) : (
                            <tr>
                                <td
                                    className="text-center text-lg font-semibold pt-20"
                                    colSpan={8} // ✅ 1 colonne de plus (Statut)
                                >
                                    {search
                                        ? "Oups, aucun bon ne correspond à votre recherche 🤭"
                                        : `Aucun ${getCouponTypeLabel().toLowerCase()} pour le moment`}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {/* Pagination */}
                {currentState.initialized &&
                    (currentState.loading || currentState.nextToken) && (
                        <div className="flex justify-center my-6">
                            <button
                                disabled={currentState.loading || !currentState.nextToken}
                                onClick={handleLoadMore}
                                className="px-5 py-2 border border-blue-admin rounded-xl text-blue-admin hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                {currentState.loading
                                    ? 'Chargement...'
                                    : currentState.nextToken
                                        ? 'Charger plus'
                                        : 'Plus de résultats'}
                            </button>
                        </div>
                    )}
            </div>

            {/* Modal suppression */}
            <ReactModal
                isOpen={isDeleteModalOpen}
                onRequestClose={closeDeleteModal}
                className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto outline-none mt-80"
                overlayClassName="fixed inset-0 bg-black/40 flex items-start justify-center z-50"
            >
                <h2 className="text-lg font-semibold mb-2">
                    Confirmer la suppression
                </h2>

                <p className="text-sm text-gray-700 mb-6">
                    Voulez-vous vraiment supprimer le bon n°{' '}
                    <span className="font-semibold">
                        {couponToDelete?.numero || couponToDelete?.id}
                    </span>
                    {' '}?
                </p>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={closeDeleteModal}
                        className="px-4 py-2 rounded-lg border text-sm"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={confirmDeleteCoupon}
                        className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700"
                    >
                        Supprimer
                    </button>
                </div>
            </ReactModal>
        </section>
    );
};

export default Coupons;
